// ui/SettingsPanel.jsx: M2.c-8 settings overlay (synthesis §4).
//
// Native <dialog> so the platform handles focus trap, Escape dismiss,
// and aria-modal. Controls are real <input>/<select> elements, every
// toggle is keyboard-accessible by default.
//
// CRT options are rendered but only Off is selectable in M2; Subtle and
// Full carry an inline "(coming in M5)" tag and are disabled. Plan v2
// requires the options to be visible now so the feature surface is
// discoverable before M5.
//
// B2-2: the CRT effect shipped (crt-shader.js + crt.css), so every tier
// is selectable now. Option data lives in settings-options.js (pure .js
// so Node tests can import it without a JSX transform).

import { useEffect, useRef } from 'preact/hooks';
import { CRT_OPTIONS, resolveShowStickTheDealer } from './settings-options.js';

// Restyle 3A: the panel's look lives in styles/cream-screens.css
// (.settings-panel and its parts), so the pixel fonts, the system-font
// switch and the focus rings reach every part. Only the in-game gear
// keeps its old inline paint (see SettingsGearButton).
const GEAR_DARK_BG     = 'rgba(42, 42, 42, 0.85)';
const GEAR_DARK_LABEL  = '#F5F5F0';
const GEAR_DARK_BORDER = '#3A3A3A';

// The cream gear: an 8 x 8 pixel cog (four teeth, four corner teeth,
// a hollow hub), drawn at 3 px per pixel.
const GEAR_ROWS = [
  'X..XX..X',
  '.XXXXXX.',
  '.XX..XX.',
  'XX....XX',
  'XX....XX',
  '.XX..XX.',
  '.XXXXXX.',
  'X..XX..X',
];
const GEAR_PIXELS = GEAR_ROWS.flatMap((row, y) => [...row].map((c, x) => (c === 'X' ? `M${x} ${y}h1v1h-1z` : ''))).join('');

const SOUND_TIER_OPTIONS = [
  { value: 'all',   label: 'All',   help: 'Music, ambience, and SFX.' },
  { value: 'vital', label: 'Vital', help: 'Trick won + hand over only.' },
  { value: 'off',   label: 'Off',   help: 'No sound output.' },
];

/**
 * @param {{
 *   open: boolean,
 *   settings: import('../settings.js').Settings,
 *   onUpdate: (patch: Partial<import('../settings.js').Settings>) => void,
 *   onClose: () => void,
 *   showStickTheDealer?: boolean,
 * }} props
 */
export function SettingsPanel({ open, settings, onUpdate, onClose, showStickTheDealer = false }) {
  /** @type {{ current: HTMLDialogElement|null }} */
  const dialogRef = useRef(null);

  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (open) {
      if (typeof d.showModal === 'function') d.showModal();
      else d.setAttribute('open', '');
    } else {
      if (typeof d.close === 'function') d.close();
      else d.removeAttribute('open');
    }
  }, [open]);

  return (
    <dialog
      ref={dialogRef}
      class="settings-panel cream"
      data-modal="settings"
      role="dialog"
      aria-modal="true"
      aria-labelledby="settings-title"
      onClose={onClose}
      onCancel={onClose}
    >
      <div class="settings-panel__inner">
        <div class="settings-panel__head">
          <h2 id="settings-title" class="settings-panel__title">Settings</h2>
          <button
            type="button"
            class="settings-panel__close"
            data-action="close-settings"
            onClick={onClose}
            aria-label="Close settings"
          >
            {/* Silkscreen's "×" is a flat bar; its "X" reads as close. */}
            X
          </button>
        </div>

        {/* Stick the Dealer (B2-3: dev hot-seat only; online it is a
            room setting owned by the host, shown in RoomSettings) */}
        {resolveShowStickTheDealer(showStickTheDealer) && (
        <Row
          label="Stick the Dealer"
          labelFor="settings-stick-the-dealer"
          checkbox
          help="If everyone passes in round 2, the dealer must call a trump."
          helpId="std-help"
        >
          <input
            id="settings-stick-the-dealer"
            type="checkbox"
            data-setting="stick_the_dealer"
            aria-label="Stick the Dealer"
            aria-describedby="std-help"
            checked={settings.stick_the_dealer}
            onChange={(e) =>
              onUpdate({ stick_the_dealer: /** @type {HTMLInputElement} */ (e.currentTarget).checked })
            }
            class="settings-check"
          />
        </Row>
        )}

        {/* Sound tier */}
        <Row
          label="Sound"
          labelFor="settings-sound-tier"
          help="All sounds, only vital cues, or silent."
          helpId="sound-help"
        >
          <select
            id="settings-sound-tier"
            data-setting="sound_tier"
            aria-describedby="sound-help"
            value={settings.sound_tier}
            onChange={(e) =>
              onUpdate({ sound_tier: /** @type {any} */ (/** @type {HTMLSelectElement} */ (e.currentTarget).value) })
            }
            class="settings-select"
          >
            {SOUND_TIER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value} title={opt.help}>{opt.label}</option>
            ))}
          </select>
        </Row>

        {/* Reduced motion override */}
        <Row
          label="Reduce motion"
          labelFor="settings-reduced-motion"
          checkbox
          help="Force reduced-motion even if your OS doesn't request it."
          helpId="rm-help"
        >
          <input
            id="settings-reduced-motion"
            type="checkbox"
            data-setting="reduced_motion_override"
            aria-label="Reduce motion"
            aria-describedby="rm-help"
            checked={settings.reduced_motion_override}
            onChange={(e) =>
              onUpdate({ reduced_motion_override: /** @type {HTMLInputElement} */ (e.currentTarget).checked })
            }
            class="settings-check"
          />
        </Row>

        {/* System font */}
        <Row
          label="System font"
          labelFor="settings-system-font"
          checkbox
          help="Swap the pixel monospace for your OS's default font."
          helpId="font-help"
        >
          <input
            id="settings-system-font"
            type="checkbox"
            data-setting="system_font"
            aria-label="System font"
            aria-describedby="font-help"
            checked={settings.system_font}
            onChange={(e) =>
              onUpdate({ system_font: /** @type {HTMLInputElement} */ (e.currentTarget).checked })
            }
            class="settings-check"
          />
        </Row>

        {/* Heuristic disclosure */}
        <Row
          label="Show bot reasoning"
          labelFor="settings-heuristic-disclosure"
          checkbox
          help="A short tag explains each bot bid (on for the first 10 hands)."
          helpId="heur-help"
        >
          <input
            id="settings-heuristic-disclosure"
            type="checkbox"
            data-setting="heuristic_disclosure"
            aria-label="Show bot reasoning"
            aria-describedby="heur-help"
            checked={settings.heuristic_disclosure}
            onChange={(e) =>
              onUpdate({ heuristic_disclosure: /** @type {HTMLInputElement} */ (e.currentTarget).checked })
            }
            class="settings-check"
          />
        </Row>

        {/* CRT effect (B2-2: every tier is live) */}
        <Row
          label="CRT effect"
          help="Scanlines + glow."
          helpId="crt-help"
          stacked
        >
          <div class="settings-radios" role="radiogroup" aria-describedby="crt-help">
            {CRT_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                class={opt.disabled ? 'settings-radio is-disabled' : 'settings-radio'}
                for={`settings-crt-${opt.value}`}
              >
                <input
                  id={`settings-crt-${opt.value}`}
                  type="radio"
                  name="crt"
                  data-setting="crt"
                  data-setting-value={opt.value}
                  value={opt.value}
                  checked={settings.crt === opt.value}
                  disabled={opt.disabled}
                  aria-disabled={opt.disabled}
                  onChange={(e) =>
                    onUpdate({ crt: /** @type {any} */ (/** @type {HTMLInputElement} */ (e.currentTarget).value) })
                  }
                  class="settings-check"
                />
                {opt.label}
                {opt.tag && <span class="settings-radio__tag">{opt.tag}</span>}
              </label>
            ))}
          </div>
        </Row>

        <div class="settings-panel__foot">
          <button
            type="button"
            class="settings-panel__done"
            data-action="done-settings"
            onClick={onClose}
          >
            Done
          </button>
        </div>
      </div>
    </dialog>
  );
}

/**
 * One settings row: label and help on the left, the control on the
 * right. A checkbox row is one big label (the whole row is the tap
 * target, U3). A stacked row puts its control on its own line below the
 * text (the CRT radios, so they never squeeze the label column).
 *
 * @param {{ label: string, labelFor?: string|null, checkbox?: boolean, stacked?: boolean, help: string, helpId: string, children?: any }} props
 */
function Row({ label, labelFor = null, checkbox = false, stacked = false, help, helpId, children }) {
  const rowClass = stacked ? 'settings-row settings-row--stacked' : 'settings-row';
  if (checkbox) {
    return (
      <label class={`${rowClass} settings-row--check`} for={labelFor || undefined}>
        <span class="settings-row__text">
          <span class="settings-row__label">{label}</span>
          <span class="settings-row__help" id={helpId}>{help}</span>
        </span>
        <span class="settings-row__control">{children}</span>
      </label>
    );
  }
  return (
    <div class={rowClass}>
      <div class="settings-row__text">
        {labelFor ? <label class="settings-row__label" for={labelFor}>{label}</label>
          : <div class="settings-row__label">{label}</div>}
        <div class="settings-row__help" id={helpId}>{help}</div>
      </div>
      <div class="settings-row__control">{children}</div>
    </div>
  );
}

/**
 * Floating gear button used by App.jsx to open the panel. Lives here
 * so the settings UI is a single import. The default 'dark' variant is
 * the in-game header's gear and keeps its inline paint; the 'cream'
 * variant (home and room header) is painted by cream-screens.css.
 *
 * @param {{ onClick: () => void, style?: any, variant?: 'dark'|'cream' }} props
 */
export function SettingsGearButton({ onClick, style = {}, variant = 'dark' }) {
  if (variant === 'cream') {
    return (
      <button
        type="button"
        class="settings-gear"
        data-action="open-settings"
        aria-label="Open settings"
        onClick={onClick}
      >
        <svg width="24" height="24" viewBox="0 0 8 8" aria-hidden="true" focusable="false" shape-rendering="crispEdges">
          <path fill="currentColor" d={GEAR_PIXELS} />
        </svg>
      </button>
    );
  }
  return (
    <button
      type="button"
      data-action="open-settings"
      aria-label="Open settings"
      onClick={onClick}
      style={{
        background: GEAR_DARK_BG,
        color: GEAR_DARK_LABEL,
        border: `1px solid ${GEAR_DARK_BORDER}`,
        borderRadius: 6,
        // U4: the gear is a phone tap target, 44x44.
        width: 44,
        height: 44,
        minWidth: 44,
        minHeight: 44,
        cursor: 'pointer',
        fontSize: 18,
        lineHeight: 1,
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...style,
      }}
    >
      <span aria-hidden="true">⚙</span>
    </button>
  );
}
